import React, { useState, useEffect, createRef } from 'react';
import csrfToken from 'ca-environment/csrfToken';
import request from 'ca-request';
import notifySavingError from 'ca-capture/helpers/notifySavingError';
import {
  RatingScale as RatingScaleType,
  ScaleValueResponse,
  ScaleValueRequest,
  ScaleItem,
} from 'ca-capture/types';
import RatingActions from '../RatingActions/RatingActions';
import styles from '../RatingQuestion.scss';
import Submission from '../../Submission/Submission';
import { LikertScaleLegacy } from '@kaizen/components';
import type { ColorSchema } from '@kaizen/components/src/LikertScaleLegacy/types';

type Props = {
  questionId: string;
  questionText: string;
  questionMandatory: boolean;
  defaultCommentContent: string;
  saveUrl: string;
  localeDirection: 'ltr' | 'rtl';
  allowComments: boolean;
  scale: RatingScaleType;
  scaleColorSchema: ColorSchema;
  existingAnswer: ScaleValueResponse;
  showAdministratorCanReplyToCommentMessage: boolean;
  uiText: {
    addComment: string;
    editComment: string;
    addCommentAria: string;
    editCommentAria: string;
    editCommentAriaDescription: string;
    dialogHeader: string;
    submitLabel: string;
    submittingLabel: string;
    dismissLabel: string;
    repliesInfo: string;
    savingError: string;
    mandatoryError: string;
    dismissWarningTitle: string;
    dismissWarningBody: string;
    dismissWarningConfirm: string;
    dismissWarningCancel: string;
  };
};

export const FivePointRatingQuestion = (
  props: Props
): React$Element<string> => {
  const {
    questionId,
    questionText,
    questionMandatory,
    defaultCommentContent,
    saveUrl,
    localeDirection,
    allowComments,
    uiText,
    scale,
    scaleColorSchema,
    existingAnswer,
    showAdministratorCanReplyToCommentMessage,
  } = props;
  const questionHeaderId = `question-header-${questionId}`;
  const questionLabel = questionMandatory ? `${questionText} *` : questionText;
  const [selectedItem, setSelectedItem] = useState<ScaleItem | null>(
    scale.find((s) => s.value === existingAnswer) || null
  );
  const containerRef = createRef<HTMLDivElement>();
  const [validationError, setValidationError] = useState<boolean>(false);

  // Without having a React container around the Capture form yet, we're listening for a custom event
  // sent from app/assets/javascripts/response.js to signal that submit has been pressed and we want to show validation errors.
  useEffect(() => {
    const checkAndSetError = (_event: any, questionIds: string[]) => {
      const answered = questionIds.indexOf(questionId) === -1;
      setValidationError(!answered);
    };
    if (questionMandatory) {
      $(document).on('promptUnanswered', checkAndSetError);
    }
    return () => {
      $(document).off('promptUnanswered', checkAndSetError);
    };
  }, [questionId, questionMandatory]);

  const onRatingSelect = (item: ScaleItem | null) => {
    const oldSelectedItem = selectedItem;
    setSelectedItem(item);
    const requestValue: ScaleValueRequest =
      item && item.value !== -1 ? item.value : '';

    if (typeof requestValue === 'number' && requestValue > 0) {
      setValidationError(false);
    }

    const data = {
      authenticity_token: csrfToken(),
      answer: requestValue,
    };

    Submission.enqueue(() => {
      return request
        .put(saveUrl, {
          body: JSON.stringify(data),
        })
        .catch((err) => {
          notifySavingError(
            err,
            saveUrl,
            'heatbar answer request',
            uiText.savingError
          );

          // Reset state
          setSelectedItem(oldSelectedItem);
        });
    });

    // Trigger jQuery for some adjustments to the .question container that we can't make here yet
    $(document).trigger('reactRatingSave', {
      container: containerRef.current,
      value: requestValue,
    });
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.flexContainer}>
        <div className={styles.label}>
          <p id={questionHeaderId}>{questionLabel}</p>
        </div>

        <div
          className={
            validationError ? styles.scaleValidationError : styles.scale
          }
        >
          <LikertScaleLegacy
            scale={scale}
            labelId={questionHeaderId}
            colorSchema={scaleColorSchema}
            data-testid={`rating-scale-${questionId}`}
            onSelect={onRatingSelect}
            selectedItem={selectedItem}
            status={validationError ? 'error' : undefined}
            validationMessage={
              validationError ? uiText.mandatoryError : undefined
            }
          />
        </div>

        <div className={styles.actions}>
          {allowComments && (
            <RatingActions
              questionId={questionId}
              questionText={questionText}
              defaultCommentContent={defaultCommentContent}
              saveUrl={saveUrl}
              localeDirection={localeDirection}
              uiText={uiText}
              showAdministratorCanReplyToCommentMessage={
                showAdministratorCanReplyToCommentMessage
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
